import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import { FaCalendarAlt } from "react-icons/fa";

// Reusable date input with an inline calendar icon and consistent formatting
const CalendarInput = forwardRef(
  ({ value, onClick, placeholder, className, disabled, id, error }, ref) => {
    const baseClasses =
      "w-full rounded-lg border-2 bg-white py-2.5 pl-3 pr-10 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out font-medium shadow-sm min-h-[44px]";
    
    const focusClasses =
      "focus:border-[#3b82f6] focus:ring-2 focus:ring-[rgba(59,130,246,0.1)] focus:shadow-lg focus:bg-white";
    
    const errorClasses = error
      ? "border-[#ef4444] focus:border-[#ef4444] focus:ring-2 focus:ring-[rgba(239,68,68,0.1)] bg-red-50/30"
      : "border-[#d1d5db] hover:border-[#9ca3af] hover:shadow-md hover:bg-white";
    
    const disabledClasses = disabled
      ? "bg-[#f3f4f6] cursor-not-allowed text-gray-400 border-[#d1d5db] shadow-none"
      : "";

    return (
      <div className={`relative ${disabled ? "opacity-70" : ""}`}>
        <input
          id={id}
          ref={ref}
          onClick={onClick}
          value={value || ""}
          readOnly
          placeholder={placeholder || "mm/dd/yyyy"}
          disabled={disabled}
          className={`${baseClasses} ${focusClasses} ${errorClasses} ${disabledClasses} ${className || ""}`}
        />
        <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3b82f6] pointer-events-none w-5 h-5" />
      </div>
    );
  }
);

CalendarInput.displayName = 'CalendarInput';

CalendarInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  error: PropTypes.bool,
};

const DateInput = ({
  selected,
  onChange,
  placeholderText = "mm/dd/yyyy",
  dateFormat = "MM/dd/yyyy",
  className = "",
  minDate,
  maxDate,
  showTimeSelect = false,
  readOnly = false,
  id,
  error = false,
  ...rest
}) => {
  return (
    <DatePicker
      selected={selected}
      onChange={onChange}
      placeholderText={placeholderText}
      dateFormat={dateFormat}
      minDate={minDate}
      maxDate={maxDate}
      showTimeSelect={showTimeSelect}
      readOnly={readOnly}
      disabled={readOnly}
      customInput={<CalendarInput className={className} id={id} error={error} disabled={readOnly} />}
      onKeyDown={(e) => {
        // prevent manual typing to keep format consistent
        e.preventDefault();
      }}
      popperClassName="react-datepicker-popper-custom"
      popperPlacement="bottom-start"
      {...rest}
    />
  );
};

DateInput.propTypes = {
  selected: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  placeholderText: PropTypes.string,
  dateFormat: PropTypes.string,
  className: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  showTimeSelect: PropTypes.bool,
  readOnly: PropTypes.bool,
  id: PropTypes.string,
  error: PropTypes.bool,
};

export default DateInput;